import { useMemo } from 'react';
import { pipe } from 'fp-ts/function';
import { array, string } from 'fp-ts';
import { useFormContext } from 'react-hook-form';
import { useFeatureFlag, useLocalizeText } from '@mews/framework';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { SpacedColon, SpacedPlusSign } from '@mews-commander/ui';
import { ResourceCategory } from '@mews-commander/data-access';
import { Option } from '@mews-commander/types';
import { getSortedResourceCategories } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { useCommanderSelector } from '@mews-commander/utils';
import { AvailabilityAdjustmentFormValues } from '../../../../types';

export const useAvailableResourceCategoryOptions = (serviceId: string, rowIndex: number) => {
    const { watch } = useFormContext<AvailabilityAdjustmentFormValues>();
    const lt = useLocalizeText();
    const fields = watch();
    const occupancyInBlocksEnabledStage2 = useFeatureFlag(FeatureFlag.occupancyInBlocksEnabledStage2);
    const occupancyInBlocksEnabled = useFeatureFlag(FeatureFlag.occupancyInBlocksEnabled);

    const categories = useCommanderSelector(
        (state) => getSortedResourceCategories(state, { serviceId }) as ResourceCategory[]
    );

    const categoryOptions: Option[] = categories.map((c) => {
        let secondaryText: string | undefined;

        if (occupancyInBlocksEnabled) {
            secondaryText = `${lt({ textKey: TextKey.Capacity })}${SpacedColon}${c.capacity}`;
            if (c.extraCapacity) {
                secondaryText += `${SpacedPlusSign}${c.extraCapacity}`;
            }
        }
        return {
            label: c.name,
            value: c.id,
            secondaryText,
            disabled: false,
            inactive: false,
            indent: false,
        };
    });

    return useMemo(() => {
        const usedResourceCategoryIds = pipe(
            fields.fieldArrayAvailability,
            array.map((f) => f.resourceCategoryId),
            array.uniq(string.Eq)
        );

        if (occupancyInBlocksEnabledStage2) {
            return categoryOptions;
        }

        const { resourceCategoryId } = fields.fieldArrayAvailability[rowIndex];

        if (categoryOptions.length === usedResourceCategoryIds.length && resourceCategoryId) {
            const found = categoryOptions.find(({ value }) => value === resourceCategoryId);
            return found ? [found] : [];
        }

        const resourceCategoryIds = usedResourceCategoryIds.filter((id) => id !== resourceCategoryId);

        return categoryOptions.filter(({ value }) => value && !resourceCategoryIds.includes(value));
    }, [fields, occupancyInBlocksEnabledStage2, rowIndex, categoryOptions]);
};
